<script setup lang="ts">
import { onMounted, ref, computed } from "vue";
import { RouterView } from "vue-router";
import MenuItem from "./components/MenuItem.vue";
import { useRouter } from "vue-router";
import { useCartStore } from "./stores/cart";
import { useAuthStore } from "./stores/auth";
import { useProfileStore } from "./stores/profile";
import Toast from "primevue/toast";
import ConfirmDialog from "primevue/confirmdialog";
import Select from "primevue/select";
import { formatOrganizationName } from "@/api/users.ts";
import { initializeAuthenticatedStores } from "@/utils/utils.ts";
import { useRefresh } from "@/utils/useRefresh.ts";
import { useProlonged } from "@/utils/useProlonged.ts";

const router = useRouter();
const cartStore = useCartStore();
const authStore = useAuthStore();
const profileStore = useProfileStore();
const { triggerRefresh } = useRefresh();

const isLoading = ref(false);
const error = ref("");

const prolongedCartLoading = useProlonged(
  computed(() => cartStore.loading),
  500,
);

async function selectOrganizationId(newOrgId: number) {
  isLoading.value = true;
  profileStore.selectOrganizationId(newOrgId);
  // Reload all related stored (products, etc.)
  await initializeAuthenticatedStores();
  isLoading.value = false;
  // trigger refresh of all components that depend on the organization
  triggerRefresh();
}

// Register event listeners
onMounted(async () => {
  // Initialize products
  isLoading.value = true;

  // make sure the router is ready before doing any AJAX requests that possibly redirect via router to login page
  await router.isReady();

  // if current route has metadata skipAuth == true, skip this initialization
  if (router.currentRoute.value.meta.skipAuth) {
    isLoading.value = false;
    return;
  }

  try {
    await initializeAuthenticatedStores();
  } catch (err: any) {
    if (err.response && err.response.status === 401) {
      return;
    } else {
      error.value = "Došlo k chybě při načítání ze serveru";
      console.error("Error initializing:", err);
    }
  } finally {
    isLoading.value = false;
  }
});
</script>

<template>
  <header class="sticky top-0 z-50">
    <div>
      <template v-if="authStore.isAuthenticated">
        <div class="main-navigation shadow-md bg-gf-light">
          <div class="max-page-width p-3">
            <div class="flex justify-between space-x-0 md:space-x-2 md:justify-start">
              <div
                class="flex-1 md:flex-none flex items-start md:items-center justify-start md:justify-center px-1 md:px-3 pt-2 pb-1 md:pb-2 rounded-md text-gray-700 menu-item"
              >
                <div
                  class="flex flex-1 items-center space-x-0 md:space-x-2 md:space-y-0 flex-col md:flex-row relative"
                >
                  <span class="flex justify-center items-center"
                    ><img src="/android-chrome-512x512.png" class="h-[18px] self-center" alt="Logo"
                  /></span>
                </div>
              </div>
              <MenuItem
                label="Kategorie"
                icon="pi-objects-column"
                goto="/categories"
                activePrefix="/category"
              />
              <MenuItem label="Historie objednávek" icon="pi pi-list" goto="/orders" />
              <MenuItem label="Akce a novinky" icon="pi pi-tag" goto="/special-category/sale" />
              <!--              <MenuItem label="Oblíbené" icon="pi pi-heart" goto="/special-category/favorite" />-->
              <MenuItem
                label="Uživatel"
                icon="pi pi-user"
                goto="/user/invoices"
                activePrefix="/user"
              />
              <MenuItem
                label="Košík"
                icon="pi pi-shopping-cart"
                :badge="cartStore.productCount ? cartStore.productCount.toString() : undefined"
                :loading="prolongedCartLoading"
                goto="/cart"
              />
            </div>
            <Select
              v-if="profileStore.organizations.length > 1"
              :options="profileStore.organizations"
              :optionLabel="formatOrganizationName"
              :modelValue="profileStore.selectedOrganizationId"
              :disabled="isLoading"
              @valueChange="selectOrganizationId"
              optionValue="id"
              class="w-full mt-2"
            >
              <template #dropdownicon>
                <i class="pi pi-arrow-right-arrow-left" />
              </template>
              <template #header>
                <div class="font-medium p-3">Přepnutí na jinou firmu:</div>
              </template>
            </Select>
          </div>
        </div>
      </template>
    </div>
  </header>

  <div v-if="isLoading" class="flex justify-center items-center p-4">
    <i class="pi pi-spin pi-spinner text-2xl"></i>
    <span class="ml-2">Načítání...</span>
  </div>

  <div v-if="error" class="p-4 m-4 bg-red-100 text-red-700 rounded-md">
    {{ error }}
  </div>

  <RouterView />

  <Toast />
  <ConfirmDialog></ConfirmDialog>
</template>

<style scoped></style>
